import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import ShowIf from '@Atoms/ShowIf';
import { useGetLessonDetail } from '@components/Component/Lesson/LessonContainer/hooks/query';
import { useHandleFinishStepOne } from '@components/Component/Lesson/LessonContainer/hooks';
import ShadowScrollbar from '@components/Scrollbar/ShadowScrollbars';
import { useIsTeacher, useUser } from '@reducers/user/hooks';
import { useParams } from 'react-router-dom';
import { useCurrentCourse } from '@reducers/courses/hooks';
import {
  usePostLessonSessionActivity,
  usePostUserReportIssue,
} from '@components/Component/Lesson/LessonContainer/hooks/mutations';
import AttendanceCard from '@components/Component/Lesson/AttendanceRecordButton/AttendanceCard';
import PacingGuideInfoCard from '@components/Component/Lesson/PacingGuide/PacingGuideInfoCard';
import { useGetPacingGuideLessons } from '@components/Component/Lesson/PacingGuide/hooks';
import useGetLearnHubSlides from '@components/LessonDetailTab/hooks/useGetLearnHubSlides';
import { DefaultContentWrap } from '../Generals/stylesheets/General.style';
import LessonReportModal from './LessonReportModal';
import {
  LessonManagementWrap,
  LessonManagementLeftWrap,
  LessonManagementRightWrap,
  LessonVideoTitlte,
  LessonHeaderContainer,
} from '../LessonManagement/LessonManagement.style';
import LessonDetailItem from './LessonDetailItem';
import LessonComplete from './LessonComplete';
import ListLoader from '../LoadingPlaceholder/ListLoader';
import {
  FeedbackContainer,
  FeedbackIcon,
  AudioNarrationWrap,
  AudioNarrationLabel,
  AudioNarrationPills,
  AudioNarrationPill,
  SubLessonSlidesCard,
  SubLessonSlidesHeader,
  SubLessonSlidesHeaderRow,
  SubLessonSlidesTitle,
  SubLessonSlidesCount,
  SubLessonSlidesHint,
  SubLessonSlidesList,
  SubLessonNextPartButton,
} from './LessonDetailTab.style';
import LoadingIndicator from '../LoadingIndicator';
import LessonSlidesViewer from './LessonSlidesViewer';
import SlideControls from './LearnhubSlides/SlideControls';
import { getSubLessons, getNextSubLesson } from './lessonDetailTabUtils';

const scrollStyle = {
  height: 'calc(100vh - 13.5em)',
  width: '100%',
  minWidth: '18em',
};

const scrollStyleMobile = {
  height: 'calc(50vh - 10em)',
  width: '100%',
  marginTop: '1em',
  order: 1,
};

class LessonDetailTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedUrl: '',
      selectedLessonNodeId: '',
      selectedSlide: 1,
      defaultPresentationId: '',
      copyPresentationId: '',
      currentPresentation: '',
      isOpen: false,
      isLoading: false,
      selectedAudioResourceId: null,
    };
  }

  componentDidMount() {
    const { lessonDetail, isTeacher } = this.props;
    const subLessons = getSubLessons(lessonDetail);

    // Open the first lesson if there is only one lesson
    if (subLessons?.length === 1) {
      const expectedUrl = isTeacher
        ? subLessons[0]?.instructor_url
        : subLessons[0]?.student_url;

      this.openUrl(expectedUrl, subLessons[0]?.id);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      isOpen,
      isLoading,
      selectedUrl,
      selectedLessonNodeId,
      currentPresentation,
      copyPresentationId,
      selectedSlide,
    } = this.state;

    if (
      _isEqual(nextProps, this.props) &&
      _isEqual(nextState.isOpen, isOpen) &&
      _isEqual(nextState.isLoading, isLoading) &&
      _isEqual(nextState.selectedUrl, selectedUrl) &&
      _isEqual(nextState.selectedLessonNodeId, selectedLessonNodeId) &&
      _isEqual(nextState.currentPresentation, currentPresentation) &&
      _isEqual(nextState.copyPresentationId, copyPresentationId) &&
      _isEqual(nextState.selectedSlide, selectedSlide)
    ) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      lessonId,
      copyPresentationId: propCopyPresentationId,
      lessonDetail,
      isTeacher,
    } = this.props;

    const { lessonId: prevLessonId } = prevProps;

    const { selectedUrl } = this.state;
    const { selectedSlide, defaultPresentationId, copyPresentationId } = this.state;
    const { selectedSlide: prevSelectedSlide } = prevState;
    const subLessons = getSubLessons(lessonDetail);
    if (lessonId !== prevLessonId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        selectedUrl: '',
        selectedLessonNodeId: '',
        defaultPresentationId: '',
        currentPresentation: '',
        selectedAudioResourceId: null,
      });
    }

    if (selectedSlide !== prevSelectedSlide) {
      if (selectedSlide === 1) {
        this.setState({ currentPresentation: defaultPresentationId });
      } else {
        this.setState({ currentPresentation: copyPresentationId });
      }
    }

    // this was removed to make the toggling between versions on reload
    // if (propCopyPresentationId !== prevCopyPresentationId) {
    this.setState({
      copyPresentationId: propCopyPresentationId,
    });
    if (propCopyPresentationId === undefined) {
      this.setState({ selectedSlide: 1 });
    }
    // }
    // the logic for select the lesson on the lesson buttons
    // it is not correct , and we have removed the render for that component
    // when there is only one lesson
    // so we have added this logic to select the first lesson
    if (selectedUrl === '' && subLessons?.length === 1) {
      this.setState({ selectedLessonNodeId: subLessons[0]?.id });
      const expectedUrl = isTeacher
        ? subLessons[0]?.instructor_url
        : subLessons[0]?.student_url;
      this.openUrl(expectedUrl || subLessons[0]?.instructor_url, subLessons[0]?.id);
    }
  }

  getLessonGoogleDriveIdByUrl = (url) => {
    if (url?.includes('drive.google.com')) {
      return url?.match(/[-\w]{25,}/)?.shift();
    }
    return null; // for s3 URLs, we will use the url as is
  };

  openUrl = (url, lessonId, audioResource = null) => {
    const googleDrivePresentationId = this.getLessonGoogleDriveIdByUrl(url);
    this.setState({
      selectedUrl: url,
      selectedLessonNodeId: lessonId,
      defaultPresentationId: googleDrivePresentationId,
      currentPresentation: googleDrivePresentationId,
      selectedAudioResourceId: audioResource ? audioResource.id : null,
    });
    const {
      sectionId,
      unitId,
      postLessonActivity,
      userId,
      lessonDetail,
      handleIsFinishStepOne,
      isTeacher,
    } = this.props;
    const body = {
      unit: unitId,
      lesson: lessonId,
      section: sectionId,
      user: userId,
      lesson_group: lessonDetail?.id,
    };
    if (!isTeacher && handleIsFinishStepOne) {
      handleIsFinishStepOne(lessonId, true);
    }
    // set to ChalkTalk Version tab when switching between lesson slides
    this.setState({ selectedSlide: 1 });
    postLessonActivity(body);
  };

  selectAudioResource = (lesson, resourceId = null) => {
    const { isTeacher } = this.props;

    if (!resourceId) {
      const defaultUrl = isTeacher ? lesson.instructor_url : lesson.student_url;
      this.openUrl(defaultUrl, lesson.id, null);
      return;
    }

    const selectedResource = lesson.audio_resources?.find(
      (resource) => String(resource.id) === String(resourceId),
    );

    if (selectedResource) {
      const url = isTeacher
        ? selectedResource.instructor_url
        : selectedResource.student_url;
      this.openUrl(
        url || (isTeacher ? lesson.instructor_url : lesson.student_url),
        lesson.id,
        selectedResource,
      );
    }
  };

  renderAudioNarrationSelector = (lesson) => {
    const { selectedAudioResourceId } = this.state;
    const audioResources = _get(lesson, 'audio_resources', []);

    if (!audioResources || audioResources.length === 0) {
      return null;
    }

    return (
      <AudioNarrationWrap role="radiogroup" aria-label="Audio narration">
        <AudioNarrationLabel>Audio Narration</AudioNarrationLabel>
        <AudioNarrationPills>
          <AudioNarrationPill
            type="button"
            role="radio"
            $isActive={!selectedAudioResourceId}
            aria-checked={!selectedAudioResourceId}
            onClick={() => this.selectAudioResource(lesson, null)}
          >
            None
          </AudioNarrationPill>
          {audioResources.map((resource) => {
            const isActive = String(selectedAudioResourceId) === String(resource.id);
            const label = resource.name || resource.language_code || 'Narrated';
            return (
              <AudioNarrationPill
                key={resource.id}
                type="button"
                role="radio"
                $isActive={isActive}
                aria-checked={isActive}
                onClick={() => this.selectAudioResource(lesson, resource.id)}
              >
                {label}
              </AudioNarrationPill>
            );
          })}
        </AudioNarrationPills>
      </AudioNarrationWrap>
    );
  };

  goToNextSubLesson = (nextLesson) => {
    const { isTeacher } = this.props;
    const url = isTeacher
      ? nextLesson.instructor_url
      : nextLesson.student_url || nextLesson.instructor_url;
    this.openUrl(url || nextLesson.instructor_url, nextLesson.id);
  };

  renderVideoRight = () => {
    const { selectedUrl, selectedLessonNodeId, isLoading, currentPresentation } =
      this.state;
    const { lessonDetail, isTeacher, isGHEAllowed, learnhubData } = this.props;

    const currentPresentationUrl = currentPresentation
      ? `https://drive.google.com/file/d/${currentPresentation}/preview`
      : selectedUrl;

    const subLessons = getSubLessons(lessonDetail);
    const currentLesson =
      subLessons.find((lesson) => String(lesson.id) === String(selectedLessonNodeId)) ||
      subLessons[0];
    const hasMultipleSubLessons = subLessons.length > 1;
    const { currentIndex, nextLesson } = hasMultipleSubLessons
      ? getNextSubLesson(subLessons, selectedLessonNodeId)
      : { currentIndex: 0, nextLesson: null };

    return (
      <LessonManagementRightWrap full={!subLessons.length}>
        <LessonHeaderContainer>
          <LessonVideoTitlte>
            <span>{lessonDetail?.name}</span>
            <ShowIf If={lessonDetail.id}>
              <FeedbackContainer onClick={this.openModal}>
                <FeedbackIcon src="/images/feedback.png" alt="Report feedback" />
              </FeedbackContainer>
            </ShowIf>
          </LessonVideoTitlte>
          <ShowIf
            If={
              isGHEAllowed &&
              isTeacher &&
              !!learnhubData &&
              Object.keys(learnhubData).length > 0
            }
          >
            <SlideControls
              lessonData={learnhubData?.lesson}
              reviewData={learnhubData?.review}
              isLoading={isLoading}
              setIsLoading={(value) => this.setState({ isLoading: value })}
              setPresentationSlide={(slideId) => {
                this.setState({ currentPresentation: slideId });
              }}
            />
          </ShowIf>
        </LessonHeaderContainer>
        <ShowIf If={isLoading}>
          <LoadingIndicator />
        </ShowIf>
        <ShowIf If={!isLoading}>
          {currentLesson && this.renderAudioNarrationSelector(currentLesson)}
          <LessonSlidesViewer
            currentPresentationUrl={currentPresentationUrl}
            lessonDetails={lessonDetail}
            selectedLessonNodeId={selectedLessonNodeId}
          />
          {hasMultipleSubLessons && nextLesson && (
            <SubLessonNextPartButton
              type="button"
              onClick={() => this.goToNextSubLesson(nextLesson)}
            >
              Continue to Part {currentIndex + 2}
              <i className="chalktalk-icon-bold-right" aria-hidden="true" />
            </SubLessonNextPartButton>
          )}
        </ShowIf>
      </LessonManagementRightWrap>
    );
  };

  renderSubLessonItem = (lesson, index) => {
    const { lessonActivityById, isTeacher } = this.props;
    const { selectedLessonNodeId } = this.state;
    const data = _get(lessonActivityById, `${lesson.id}`, null);
    const checked = data ? 'completed' : 'incompleted';
    return (
      <LessonDetailItem
        index={index}
        lessonDetailItem={lesson}
        key={lesson.id}
        openUrl={this.openUrl}
        checked={checked}
        isTeacher={isTeacher}
        isGrouped
        isSelected={String(lesson.id) === String(selectedLessonNodeId)}
      />
    );
  };

  onLessonChanged = (lessonId, status) => {
    const {
      sectionId,
      unitId,
      postLessonActivity,
      userId,
      lessonDetail,
      handleIsFinishStepOne,
    } = this.props;

    const subLessons = getSubLessons(lessonDetail);

    if (!subLessons[0]?.id) {
      return;
    }

    const body = {
      unit: unitId,
      lesson: subLessons[0].id,
      section: sectionId,
      user: userId,
      lesson_group: lessonId,
    };
    handleIsFinishStepOne(subLessons[0].id, status);
    postLessonActivity(body);
  };

  renderCompleteLessonItem = () => {
    const {
      lessonActivitySummaryById,
      lessonId,
      unitId,
      subjectId,
      updateSectionComplete,
      updateSectionUnComplete,
      sectionId,
    } = this.props;

    const payload = {
      unit: unitId,
      lesson: lessonId,
      subject: subjectId,
    };
    const isCompletedLesson = _get(
      lessonActivitySummaryById,
      `${lessonId}.completed`,
      false,
    );
    return (
      <LessonComplete
        key={lessonId}
        isCompletedLesson={isCompletedLesson}
        updateSectionComplete={updateSectionComplete}
        updateSectionUnComplete={updateSectionUnComplete}
        payload={payload}
        sectionId={sectionId}
        onLessonChanged={this.onLessonChanged}
      />
    );
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
    });
  };

  openModal = () => {
    this.setState({
      isOpen: true,
    });
  };

  submitModal = (msg, slideNumber) => {
    const { userReportIssue } = this.props;
    const { mutate, isSuccess } = userReportIssue;
    mutate({
      content_object_id: +slideNumber,
      content_type: 'lesson',
      content: msg,
    });
    if (isSuccess) {
      this.setState({
        isOpen: false,
      });
    }
  };

  render() {
    const { isOpen } = this.state;

    const {
      lessonDetail,
      isGettingLessonDetail,
      isTeacher,
      curriculumDetail,
      pacingGuideLessonData,
    } = this.props;

    const subLessons = getSubLessons(lessonDetail);

    return (
      <DefaultContentWrap>
        <LessonReportModal
          isOpen={isOpen}
          onClose={() => this.closeModal()}
          onSubmit={this.submitModal}
          curriculum={curriculumDetail?.name}
          lessonTitle={lessonDetail?.name}
        />
        <LessonManagementWrap>
          {isGettingLessonDetail ? (
            <ListLoader />
          ) : (
            subLessons.length > 0 && (
              <MediaQuery maxWidth={1280}>
                {(matches) => (
                  <div style={matches ? scrollStyleMobile : scrollStyle}>
                    <ShowIf If={subLessons.length > 1}>
                      <LessonManagementLeftWrap>
                        <SubLessonSlidesCard>
                          <SubLessonSlidesHeader>
                            <SubLessonSlidesHeaderRow>
                              <SubLessonSlidesTitle>Lesson Slides</SubLessonSlidesTitle>
                              <SubLessonSlidesCount>
                                {subLessons.length} parts
                              </SubLessonSlidesCount>
                            </SubLessonSlidesHeaderRow>
                            <SubLessonSlidesHint>
                              View each part in order using the list below.
                            </SubLessonSlidesHint>
                          </SubLessonSlidesHeader>
                          <SubLessonSlidesList>
                            {subLessons.map(this.renderSubLessonItem)}
                          </SubLessonSlidesList>
                        </SubLessonSlidesCard>
                      </LessonManagementLeftWrap>
                    </ShowIf>

                    <ShowIf If={isTeacher}>
                      <LessonManagementLeftWrap>
                        {this.renderCompleteLessonItem()}
                      </LessonManagementLeftWrap>
                      <LessonManagementLeftWrap>
                        <AttendanceCard onLessonChange={this.onLessonChanged} />
                      </LessonManagementLeftWrap>
                    </ShowIf>
                    <LessonManagementLeftWrap>
                      <PacingGuideInfoCard
                        guideInfoText={pacingGuideLessonData?.lesson_slides_instruction}
                        taskDeliveryText={pacingGuideLessonData?.lesson_delivery}
                      />
                    </LessonManagementLeftWrap>
                  </div>
                )}
              </MediaQuery>
            )
          )}
          {this.renderVideoRight()}
        </LessonManagementWrap>
      </DefaultContentWrap>
    );
  }
}

LessonDetailTab.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lessonDetail: PropTypes.objectOf(PropTypes.any).isRequired,
  postLessonActivity: PropTypes.func.isRequired,
  userReportIssue: PropTypes.shape({
    mutate: PropTypes.func.isRequired,
    isSuccess: PropTypes.bool,
  }).isRequired,
  sectionId: PropTypes.string.isRequired,
  subjectId: PropTypes.string,
  unitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  lessonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userId: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  lessonActivityById: PropTypes.object,
  isTeacher: PropTypes.bool,
  handleIsFinishStepOne: PropTypes.func,
  isGettingLessonDetail: PropTypes.bool,
  copyPresentationId: PropTypes.string,
  isGHEAllowed: PropTypes.bool,
};

const withDataWrapper = (WrappedComponent) => {
  const ComponentWithData = (props) => {
    const { copyPresentationId } = props;
    const handleIsFinishStepOne = useHandleFinishStepOne();
    const { lessonId, sectionId, unitId } = useParams();
    const courseData = useCurrentCourse();
    const isGHEAllowed = courseData?.allow_ghe;
    const user = useUser();
    const isTeacher = useIsTeacher();
    const { data: lessonDetail, isLoading } = useGetLessonDetail();
    const { mutate: postLessonSessionActivity } = usePostLessonSessionActivity();
    const mutationPostUserReportIssue = usePostUserReportIssue();
    const { data: pacingGuideLesson } = useGetPacingGuideLessons(lessonId);
    const pacingGuideLessonData = pacingGuideLesson?.[0];
    const { data: learnhubData } = useGetLearnHubSlides(
      user?.id,
      lessonDetail?.lessons?.[0]?.id,
    );

    return (
      <ShadowScrollbar
        autoHide={false}
        style={{
          height: 'calc(100vh - 162px)',
          margin: 0,
        }}
      >
        <WrappedComponent
          {...props}
          lessonId={lessonId}
          lessonDetail={lessonDetail}
          isTeacher={isTeacher}
          isGettingLessonDetail={isLoading}
          isGHEAllowed={isGHEAllowed}
          postLessonActivity={postLessonSessionActivity}
          userId={user?.id}
          sectionId={sectionId}
          unitId={unitId}
          handleIsFinishStepOne={handleIsFinishStepOne}
          userReportIssue={mutationPostUserReportIssue}
          pacingGuideLessonData={pacingGuideLessonData}
          copyPresentationId={copyPresentationId}
          learnhubData={learnhubData}
        />
      </ShadowScrollbar>
    );
  };
  return ComponentWithData;
};
export { LessonDetailTab };
export default withDataWrapper(LessonDetailTab);
